import {
  GRID_COLUMNS,
  frameIndex,
  normalizeGridSpec,
  type CharacterGridSpec,
} from "@/character/grid";
import {
  normalizeSkillFrameLayout,
  type SkillFrameLayout,
  type SkillFrameRect,
} from "@/character/skills/skill-frame-layout";
import { applyAutoBackgroundRemovalFromDataUrl } from "@/home/remove-white-bg";

export type ParsedSkillSheet = {
  spritesheetDataUrl: string;
  grid: CharacterGridSpec;
  iconDataUrl: string;
  frameLayout: SkillFrameLayout;
};

function compositeFrames(
  image: HTMLImageElement,
  layout: SkillFrameLayout,
): { dataUrl: string; grid: CharacterGridSpec } {
  const frames = layout.frames;
  const cellW = Math.max(1, Math.round(Math.max(...frames.map((f) => f.w))));
  const cellH = Math.max(1, Math.round(Math.max(...frames.map((f) => f.h))));
  const canvas = document.createElement("canvas");
  canvas.width = cellW * GRID_COLUMNS;
  canvas.height = cellH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("แยกเฟรมสกิลไม่สำเร็จ");

  frames.forEach((fr, i) => {
    const dx = i * cellW + (cellW - fr.w) / 2;
    const dy = cellH - fr.h;
    ctx.drawImage(
      image,
      fr.x,
      fr.y,
      fr.w,
      fr.h,
      dx,
      dy,
      fr.w,
      fr.h,
    );
  });

  const grid = normalizeGridSpec({
    columns: GRID_COLUMNS,
    rows: 1,
    frameWidth: cellW,
    frameHeight: cellH,
    regionX: 0,
    regionY: 0,
    sheetColumns: GRID_COLUMNS,
    alignX: 0,
    alignY: 0,
  });

  return {
    dataUrl: canvas.toDataURL("image/png"),
    grid,
  };
}

function frameIcon(dataUrl: string, grid: CharacterGridSpec): Promise<string> {
  return loadImage(dataUrl).then((img) => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(grid.frameWidth));
    canvas.height = Math.max(1, Math.round(grid.frameHeight));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("สร้างไอคอนสกิลไม่สำเร็จ");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  });
}

export async function parseSkillSpritesheet(
  dataUrl: string,
  layoutInput?: SkillFrameLayout,
): Promise<ParsedSkillSheet> {
  const cleanedSource = await applyAutoBackgroundRemovalFromDataUrl(dataUrl);
  const image = await loadImage(cleanedSource);
  const w = image.naturalWidth;
  const h = image.naturalHeight;
  if (w < 48 || h < 8) {
    throw new Error("ภาพเล็กเกินไป");
  }
  const layout = normalizeSkillFrameLayout(
    layoutInput?.frames?.length === GRID_COLUMNS ? layoutInput : undefined,
    w,
    h,
  );
  const { dataUrl: compositeUrl, grid } = compositeFrames(image, layout);
  const spritesheetDataUrl = await applyAutoBackgroundRemovalFromDataUrl(compositeUrl);
  const iconDataUrl = await frameIcon(spritesheetDataUrl, grid);
  return {
    spritesheetDataUrl,
    grid,
    iconDataUrl,
    frameLayout: layout,
  };
}

export function skillAnimationFrameRange(grid: CharacterGridSpec): {
  start: number;
  end: number;
} {
  const start = frameIndex(0, 0, grid.columns);
  const end = start + grid.columns - 1;
  return { start, end };
}

/** Build layout from legacy uniform grid (preview helpers). */
export function layoutFromSheetLayout(
  image: Pick<HTMLImageElement, "naturalWidth" | "naturalHeight">,
  frameWidth: number,
  frameHeight: number,
  offsetX: number,
  offsetY: number,
): SkillFrameLayout {
  const frames: SkillFrameRect[] = [];
  for (let i = 0; i < GRID_COLUMNS; i += 1) {
    frames.push({
      x: offsetX + i * frameWidth,
      y: offsetY,
      w: frameWidth,
      h: frameHeight,
    });
  }
  return normalizeSkillFrameLayout({ frames }, image.naturalWidth, image.naturalHeight);
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("โหลดภาพสกิลไม่สำเร็จ"));
    img.src = dataUrl;
  });
}
